import { Button } from "@/components/ui/button";
import { Github, Twitter, Youtube, Mail, Facebook, Instagram } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { User } from "@supabase/supabase-js";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import logoImage from "@/assets/newlogo.png";
import { Link, useNavigate } from "react-router-dom";
import { SkoolIcon } from "@/components/icons/SkoolIcon";

export function Footer() {
  const currentYear = new Date().getFullYear();
  const [user, setUser] = useState<User | null>(null);
  const {
    toast
  } = useToast();
  const {
    hasAccess,
    loading: paymentLoading
  } = usePaymentStatus();
  const navigate = useNavigate();
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth changes
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);
  const handleProtectedClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();

    // Check localStorage first for immediate access after payment
    const localPaymentCompleted = localStorage.getItem('payment_completed') === 'true';

    // If payment is loading, don't block access
    if (paymentLoading) {
      navigate(href);
      return;
    }

    // Check both hook status and localStorage
    if (!hasAccess && !localPaymentCompleted) {
      // Scroll to pricing section instead of showing toast
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({
          behavior: 'smooth'
        });
      } else {
        navigate('/', { replace: true });
        setTimeout(() => {
          const pricingSection = document.getElementById('pricing');
          pricingSection?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
      return;
    }
    navigate(href);
  };
  const scrollToCategory = (categoryId: string) => {
    // First scroll to templates section
    const templatesSection = document.getElementById('templates');
    if (templatesSection) {
      templatesSection.scrollIntoView({
        behavior: 'smooth'
      });

      // Then after a brief delay, scroll to the specific category
      setTimeout(() => {
        const categoryElement = document.getElementById(categoryId);
        if (categoryElement) {
          categoryElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 500);
    }
  };
  return <footer className="bg-card border-t border-border">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <img 
                  src={logoImage} 
                  alt="AI Auto Base" 
                  width="136"
                  height="40"
                  loading="lazy"
                  className="h-10 w-auto" 
                />
              </Link>
            </div>
            <p className="text-muted-foreground leading-relaxed">AI Automation Base is an online school run by Hector Diaz. Live follow-along classes, courses on Claude Code and n8n, and Build Packs that install a complete system on a machine you own.</p>
            <p className="text-muted-foreground text-sm">📞 <a href="tel:+16106724924" className="hover:text-primary">+1 610-672-4924</a> (610-ORBIX-AI)</p>
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="sm" className="p-2" asChild>
                <a href="https://www.facebook.com/profile.php?id=61588798776959" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Facebook">
                  <Facebook className="w-4 h-4" aria-hidden="true" />
                </a>
              </Button>
              <Button variant="ghost" size="sm" className="p-2" asChild>
                <a href="https://www.instagram.com/aiautobaseteam/" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram">
                  <Instagram className="w-4 h-4" aria-hidden="true" />
                </a>
              </Button>
              <Button variant="ghost" size="sm" className="p-2" asChild>
                <a href="https://www.youtube.com/@AIAutoBase" target="_blank" rel="noopener noreferrer" aria-label="Subscribe to our YouTube channel">
                  <Youtube className="w-4 h-4" aria-hidden="true" />
                </a>
              </Button>
              <Button variant="ghost" size="sm" className="p-2" asChild>
                <a href="mailto:info@aiautobase.com?subject=Question%20from%20aiautobase.com" aria-label="Email us at info@aiautobase.com">
                  <Mail className="w-4 h-4" aria-hidden="true" />
                </a>
              </Button>
              <Button variant="ghost" size="sm" className="p-2" asChild>
                <a href="https://www.skool.com/aiautomation-by-adam-8725/about" target="_blank" rel="noopener noreferrer" aria-label="Join our Skool community">
                  <SkoolIcon className="w-4 h-4 shrink-0 max-w-none" />
                </a>
              </Button>
            </div>
          </div>

          {/* Resources */}
          <div className="space-y-4">
            <h4 className="font-semibold">Resources</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {["Claude Code", "AntiGravity", "Open Claw", "n8n", "Other"].map((cat) => (
                <li key={cat}>
                  <button
                    onClick={() => {
                      const el = document.getElementById('resources-vault');
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth' });
                        window.dispatchEvent(new CustomEvent('resources-vault:set-category', { detail: cat }));
                      } else {
                        navigate('/');
                        setTimeout(() => {
                          const el2 = document.getElementById('resources-vault');
                          el2?.scrollIntoView({ behavior: 'smooth' });
                          window.dispatchEvent(new CustomEvent('resources-vault:set-category', { detail: cat }));
                        }, 200);
                      }
                    }}
                    className="hover:text-foreground transition-colors text-left hover:underline"
                  >
                    {cat}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* AI Growth Kit */}
          <div className="space-y-4">
            <h4 className="font-semibold">AI Growth kit</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/ai-agency-roadmap" onClick={e => handleProtectedClick(e, "/ai-agency-roadmap")} className="hover:text-foreground transition-colors cursor-pointer">AI Agency Roadmap</Link></li>
              <li><Link to="/ai-automation-roadmap" onClick={e => handleProtectedClick(e, "/ai-automation-roadmap")} className="hover:text-foreground transition-colors cursor-pointer">AI Automation Roadmap</Link></li>
              <li><Link to="/automation-money-making" onClick={e => handleProtectedClick(e, "/automation-money-making")} className="hover:text-foreground transition-colors cursor-pointer">Automation Money Making</Link></li>
              <li><Link to="/build-free-ai-agency-website" onClick={e => handleProtectedClick(e, "/build-free-ai-agency-website")} className="hover:text-foreground transition-colors cursor-pointer">Build Free AI Agency Website</Link></li>
              <li><Link to="/launch-your-ai-agency" onClick={e => handleProtectedClick(e, "/launch-your-ai-agency")} className="hover:text-foreground transition-colors cursor-pointer">Launch Your AI Agency</Link></li>
              <li><Link to="/best-ai-automation-tools" onClick={e => handleProtectedClick(e, "/best-ai-automation-tools")} className="hover:text-foreground transition-colors cursor-pointer">Best AI Automation Tools</Link></li>
            </ul>
          </div>

          {/* Blogs */}
          <div className="space-y-4">
            <h4 className="font-semibold">Blogs</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/claude-code" onClick={e => handleProtectedClick(e, "/claude-code")} className="hover:text-foreground transition-colors cursor-pointer">Claude Code</Link></li>
              <li><Link to="/antigravity" onClick={e => handleProtectedClick(e, "/antigravity")} className="hover:text-foreground transition-colors cursor-pointer">AntiGravity</Link></li>
              <li><Link to="/n8n-automation" onClick={e => handleProtectedClick(e, "/n8n-automation")} className="hover:text-foreground transition-colors cursor-pointer">n8n Automation</Link></li>
              <li><Link to="/claude-skills" onClick={e => handleProtectedClick(e, "/claude-skills")} className="hover:text-foreground transition-colors cursor-pointer">Claude Skills</Link></li>
              <li><Link to="/n8n-templates" onClick={e => handleProtectedClick(e, "/n8n-templates")} className="hover:text-foreground transition-colors cursor-pointer">n8n Templates</Link></li>
              <li><Link to="/ai-automation" onClick={e => handleProtectedClick(e, "/ai-automation")} className="hover:text-foreground transition-colors cursor-pointer">AI Automation</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div className="space-y-4">
            <h4 className="font-semibold">Explore</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/about-us" onClick={e => handleProtectedClick(e, "/about-us")} className="hover:text-foreground transition-colors cursor-pointer">About Us</Link></li>
              <li><Link to="/contact" onClick={e => handleProtectedClick(e, "/contact")} className="hover:text-foreground transition-colors cursor-pointer">Contact</Link></li>
              <li><Link to="/newsletter" className="hover:text-foreground transition-colors cursor-pointer">Newsletter</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-foreground transition-colors cursor-pointer">Privacy Policy</Link></li>
              <li><Link to="/support" onClick={e => handleProtectedClick(e, "/support")} className="hover:text-foreground transition-colors cursor-pointer">Support</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-sm text-muted-foreground">
              © {currentYear} AI Auto Base. All rights reserved.
            </div>
            <div className="flex items-center space-x-6 text-sm text-muted-foreground">
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}